// src/pace.ts
// Speed limits, so bots can't flood the site. Each post, comment, letter,
// pen pal request, kudos, and report is written together with
// pace/{uid}/kinds/{kind} = { at: <server time> }, and firestore.rules
// refuses it if your last one of that kind was too recent.
//
// Before writing, we check the wait ourselves, so a person who's just
// quick gets a friendly note (SlowDownNotice) instead of an error.
import { WriteBatch, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

// Seconds to wait between two of the same thing. Keep in step with
// paceGap() in firestore.rules.
export const PACE = { post: 60, comment: 15, letter: 30, request: 30, kudos: 10, report: 30 } as const;
export type PaceKind = keyof typeof PACE;

export const paceRef = (uid: string, kind: PaceKind) => doc(db, "pace", uid, "kinds", kind);

export class SlowDown extends Error {
  constructor(public kind: PaceKind, public wait: number) {
    super(`Please wait ${wait} more seconds.`);
  }
}
export const isSlowDown = (err: unknown): err is SlowDown => err instanceof SlowDown;

export const SLOW_DOWN_EVENT = "penpal:slow-down";

// Throws SlowDown (and shows the friendly note) if it's too soon.
export async function checkPace(uid: string, kind: PaceKind) {
  const snap = await getDoc(paceRef(uid, kind)).catch(() => null);
  const last = snap?.data()?.at?.toMillis?.() as number | undefined;
  if (!last) return;
  const wait = Math.ceil((last + PACE[kind] * 1000 - Date.now()) / 1000);
  if (wait > 0) {
    window.dispatchEvent(new CustomEvent(SLOW_DOWN_EVENT, { detail: { kind, wait } }));
    throw new SlowDown(kind, wait);
  }
}

// Adds the pace stamp to a batch that writes the post itself.
export function markPace(batch: WriteBatch, uid: string, kind: PaceKind) {
  batch.set(paceRef(uid, kind), { at: serverTimestamp() });
}
